import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class KNN extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.knn',
        group: 'Spatial Analysis',
        title: 'K-Nearest Neighbors',
        description: `
      This component performs a K-Nearest Neighbors analysis.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Points table'
        },
        {
          name: 'id',
          type: WorkflowComponentParamType.Column,
          title: 'Id column',
          parent: 'source',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'k',
          type: WorkflowComponentParamType.Number,
          title: 'Number of nearest neighbors',
          min: 1,
          optional: true,
          default: 3
        },
        {
          name: 'max_distance',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum distance',
          min: 0,
          optional: true
        }
      ]
    )
    // In BQ, the output from this component cannot be optimized
    this.inputs = this.inputs.filter((input) => !input.providers?.includes(Provider.BigQuery))
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    if (connection.type !== Provider.BigQuery) {
      return null
    }
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    const where = params.max_distance ? `WHERE distance <= ${params.max_distance} ` : ''
    const select = `
              SELECT *
              FROM UNNEST((
                  SELECT ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.KNN(myarray, CAST(${
      params.k
    } AS INTEGER))
                  FROM (
                    SELECT ARRAY_AGG(
                      STRUCT(
                        CAST(${params.id} AS STRING) AS id,
                        ${params.geo}
                      )
                    ) myarray
                    FROM ${connection.quoteName(params.source as string)}
                  )
              ))
              ${where}ORDER BY geoid`
    const query = connection.getCreateTempTableQuery(tablename, select, codeType, null)

    return {
      outputs: { result: tablename },
      query
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, connection }: GetOutputSchemaParams): TableSchema {
    const idType = columns.dryrun.find((c) => c.name === 'id')?.type
    return [
      {
        name: 'geo',
        type: connection.getGeoDataType()
      },
      {
        name: 'geo_knn',
        type: connection.getGeoDataType()
      },
      {
        name: 'distance',
        type: ColumnDataType.Number
      },
      {
        name: 'geoid',
        type: idType as ColumnDataType
      },
      {
        name: 'geoid_knn',
        type: idType as ColumnDataType
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery]
  }
}

export { KNN }
